import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CheckCircle2, MapPin, Phone, Printer, Ticket, Users } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import Header from '../../../components/Header'
import { useI18n } from '../../../i18n/useI18n'
import { api, type PublicExperienceTicket as TicketData } from '../../../services/api'
import { formatSlotDateTime } from '../../../utils/experienceSlot'

/**
 * 体験チケットの公開券面 (#1248) — 未ログインで閲覧・印刷可。
 *
 * `/t/{code}` で来訪。購入完了メールのリンクの行き先で、消し込み用 QR を表示する。
 * **ページを紙に印刷して持参しても同じ QR で消し込める**のが狙い (訪日客は
 * 通信・バッテリー切れがあるため、紙の保険が効く)。認可は code の所持
 * (発行時採番の 10 桁 Crockford Base32 ≈50bit = 消し込みと同じ bearer トークン)。
 * バックエンドは PII (memberId / 決済 ID 等) を除いた券面だけを返す。
 */
export default function PublicExperienceTicket() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { t, locale } = useI18n()
  const [data, setData] = useState<TicketData | null>(null)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!code) {
      setError(true)
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.getPublicExperienceTicket(code)
        if (!cancelled) setData(res)
      } catch {
        if (!cancelled) setError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [code])

  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) navigate(-1)
    else navigate('/home')
  }

  const name = data ? (locale === 'ja' ? data.productName : data.productNameEn || data.productName) : ''
  const provider = data ? (locale === 'ja' ? data.providerName : data.providerNameEn || data.providerName) : ''
  const redeemed = data?.status === 'redeemed'
  const total = data ? data.priceJpy * data.quantity : 0
  const mapUrl = (s: NonNullable<TicketData['store']>) =>
    s.googleMapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.address || s.name || '')}`

  return (
    <div className="min-h-screen bg-warm-white safe-top pb-20 print:bg-white print:pb-0">
      {/* 印刷時はアプリの chrome (ヘッダ/ボタン) を落として券面だけを刷る */}
      <style>{`@media print { .no-print { display: none !important } body { background: #fff } }`}</style>
      <div className="no-print">
        <Header title={t('experience.ticketTitle')} onBack={handleBack} />
      </div>

      {loading ? (
        <p className="text-center py-16 font-body text-sm text-text-tertiary">...</p>
      ) : error || !data ? (
        <div className="px-6 pt-16 text-center">
          <p className="font-display text-lg font-semibold text-text-primary mb-2">
            {t('publicReservation.error.not_found.title')}
          </p>
          <p className="font-body text-sm text-text-secondary mb-6">
            {t('publicReservation.error.not_found.detail')}
          </p>
          <button
            onClick={handleBack}
            className="rounded-xl bg-yokoso-red px-6 py-3 text-white font-display font-semibold tap-effect no-print"
          >
            {t('publicReservation.error.cta')}
          </button>
        </div>
      ) : (
        <div className="px-4 pt-4 space-y-4">
          {/* 券面: 体験名 + QR。消込済は QR を薄くしてバッジで明示 */}
          <div className="bg-white rounded-2xl shadow-card p-4 print:shadow-none print:border print:border-gray-300">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-2.5 py-0.5 text-[11px] font-body font-semibold text-gold-dark">
                <Ticket size={12} />
                {t('experience.shareTicketLabel')}
              </span>
              {redeemed && (
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2.5 py-0.5 text-[11px] font-body font-semibold text-text-secondary">
                  <CheckCircle2 size={12} />
                  {t('experience.redeemedLabel')}
                </span>
              )}
            </div>
            <p className="font-display font-bold text-lg text-text-primary mb-1">{name}</p>
            {provider && (
              <p className="font-body text-xs text-text-tertiary mb-2">{provider}</p>
            )}
            <div className="space-y-1.5 font-body text-sm text-text-primary mb-4">
              <div className="flex items-center gap-2">
                <Ticket size={16} className="text-text-secondary shrink-0" />
                <span>
                  {data.slotDate
                    ? t('experience.slotOfDay', { datetime: formatSlotDateTime(data.slotDate, data.slotStartTime, locale) })
                    : t('experience.scheduleFree')}
                  {/* #1487: 終了時刻が分かる券は併記 (いつ終わるか無しでは予定が組めない) */}
                  {data.slotEndTime ? ` 〜${data.slotEndTime}` : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Users size={16} className="text-text-secondary shrink-0" />
                <span>{t('publicReservation.partySize', { count: String(data.quantity) })}</span>
                <span className="text-text-secondary">
                  ¥{data.priceJpy.toLocaleString()} × {data.quantity} = ¥{total.toLocaleString()}
                </span>
              </div>
              {data.meetingPoint?.name && (
                <div className="flex items-center gap-2">
                  <MapPin size={16} className="text-text-secondary shrink-0" />
                  <span>
                    {t('experience.meetingPointTitle')}:{' '}
                    {data.meetingPoint.mapUrl ? (
                      <a
                        href={data.meetingPoint.mapUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-navy underline underline-offset-2"
                      >
                        {data.meetingPoint.name}
                      </a>
                    ) : (
                      data.meetingPoint.name
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* #1556: 現地払いは「まだ払っていない」を券面で明示 — 当日もめさせない */}
            {data.paymentTiming === 'onsite' && !redeemed && (
              <div className="mb-3 rounded-lg bg-gold/10 px-3 py-2 font-body text-xs font-semibold text-gold-dark">
                {t('experience.payOnsiteTicketNote', { amount: total.toLocaleString() })}
              </div>
            )}

            {/* QR = 消し込み code。画面提示でも紙でも同じ値をスキャンする */}
            <div className="flex flex-col items-center py-2">
              <div className={redeemed ? 'opacity-30' : ''}>
                <QRCodeSVG value={data.code} size={190} level="M" />
              </div>
              <p className="mt-2 font-mono text-xl tracking-[0.2em] text-text-primary">{data.code}</p>
              <p className="mt-1 font-body text-[11px] text-text-tertiary">{t('experience.ticketCode')}</p>
            </div>
            <p className="text-center font-body text-xs text-text-secondary mt-2">
              {t('experience.showStaff')}
            </p>
            <p className="text-center font-body text-[11px] text-text-tertiary mt-1">
              {t('experience.printPaperHint')}
            </p>
          </div>

          {/* Store info (紙で持参した時の連絡先・場所) */}
          <div className="bg-white rounded-2xl shadow-card p-4 print:shadow-none print:border print:border-gray-300">
            <p className="font-display font-bold text-base text-text-primary mb-1">
              {data.store.name || t('publicReservation.unknownStore')}
            </p>
            <div className="space-y-1">
              {data.store.address && (
                <p className="font-body text-xs text-text-secondary">
                  <MapPin size={12} className="inline mr-1 -mt-0.5" />
                  {data.store.address}
                </p>
              )}
              {data.store.phoneNumber && (
                <a
                  href={`tel:${data.store.phoneNumber}`}
                  className="font-body text-xs text-text-secondary flex items-center gap-1"
                >
                  <Phone size={12} className="shrink-0" />
                  {data.store.phoneNumber}
                </a>
              )}
            </div>
            {/* 現地までの導線 (画面のみ — 紙では住所テキストが既に載っている) */}
            {(data.store.googleMapsUrl || data.store.address || data.store.name) && (
              <div className="no-print mt-3">
                <a
                  href={mapUrl(data.store)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-body font-semibold text-navy tap-effect"
                >
                  <MapPin size={12} />
                  {t('publicReservation.openInMaps')}
                </a>
              </div>
            )}
          </div>

          {/* 印刷 (紙持参用)。印刷物自体にはボタンを出さない */}
          <button
            onClick={() => window.print()}
            className="no-print flex w-full h-12 items-center justify-center gap-2 rounded-xl bg-navy text-white font-display font-semibold text-sm tap-effect"
          >
            <Printer size={16} />
            {t('experience.printTicket')}
          </button>
        </div>
      )}
    </div>
  )
}
